import { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../stores/authStore'
import { useTheme } from '../contexts/ThemeContext'
import {
    LayoutDashboard,
    Calendar,
    CalendarDays,
    Users,
    Shield,
    Users2,
    LogOut,
    Menu,
    X,
    ChevronDown,
    Globe,
    Sun,
    Moon,
    Settings
} from 'lucide-react'
import './DashboardLayout.css'

export default function DashboardLayout() {
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const { user, logout, hasPermission } = useAuthStore()
    const { theme, toggleTheme } = useTheme()
    const [sidebarOpen, setSidebarOpen] = useState(true)
    const [userMenuOpen, setUserMenuOpen] = useState(false)
    const [langMenuOpen, setLangMenuOpen] = useState(false)
    const [settingsMenuOpen, setSettingsMenuOpen] = useState(false)

    const handleLogout = async () => {
        await logout()
        navigate('/login')
    }

    const changeLanguage = (lang) => {
        i18n.changeLanguage(lang)
        localStorage.setItem('language', lang)
        setLangMenuOpen(false)
    }

    const navItems = [
        {
            path: '/dashboard',
            icon: LayoutDashboard,
            label: t('nav.dashboard'),
            permission: 'MODULE_DASHBOARD'
        },
        {
            path: '/rendezvous',
            icon: Calendar,
            label: t('nav.rendezvous'),
            permission: 'MODULE_LIST_RDV'
        },
        {
            path: '/agenda',
            icon: CalendarDays,
            label: t('nav.agenda'),
            permission: 'MODULE_AGENDA'
        },
        {
            path: '/users',
            icon: Users,
            label: t('nav.users'),
            permission: 'MANAGE_USERS'
        },
        {
            path: '/profils',
            icon: Shield,
            label: t('nav.profils'),
            permission: 'MANAGE_PROFILE'
        },
        {
            path: '/equipes',
            icon: Users2,
            label: t('nav.equipes'),
            permission: 'MANAGE_EQUIPES'
        },
        {
            path: '/settings',
            icon: Settings,
            label: t('nav.settings'),
            permission: '*' // We manage fine-grained permissions inside the page
        }
    ]

    return (
        <div className={`dashboard-layout ${sidebarOpen ? '' : 'sidebar-collapsed'}`}>
            {/* Sidebar */}
            <aside className="sidebar">
                <div className="sidebar-header">
                    <div className="sidebar-logo">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                            <path d="m9 16 2 2 4-4" />
                        </svg>
                        <span className="sidebar-logo-text">Star-Dev</span>
                    </div>
                    <button
                        className="sidebar-toggle"
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                    >
                        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>

                <nav className="sidebar-nav">
                    {navItems.map((item) => {
                        if (!hasPermission(item.permission)) return null

                        if (item.children) {
                            return (
                                <div key={item.path} className="sidebar-nav-group">
                                    <button
                                        className={`sidebar-nav-item ${settingsMenuOpen ? 'active' : ''}`}
                                        onClick={() => setSettingsMenuOpen(!settingsMenuOpen)}
                                    >
                                        <item.icon size={20} />
                                        <span className="sidebar-nav-label">{item.label}</span>
                                        <ChevronDown
                                            size={16}
                                            className={`sidebar-nav-chevron ${settingsMenuOpen ? 'rotate-180' : ''}`}
                                            style={{ marginLeft: 'auto', transition: 'transform 0.2s' }}
                                        />
                                    </button>
                                    {settingsMenuOpen && (
                                        <div className="sidebar-nav-children">
                                            {item.children.map((child) => (
                                                <NavLink
                                                    key={child.path}
                                                    to={child.path}
                                                    className={({ isActive }) =>
                                                        `sidebar-nav-item submenu-item ${isActive ? 'active' : ''}`
                                                    }
                                                >
                                                    <span className="sidebar-nav-label">{child.label}</span>
                                                </NavLink>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )
                        }

                        return (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                className={({ isActive }) =>
                                    `sidebar-nav-item ${isActive ? 'active' : ''}`
                                }
                            >
                                <item.icon size={20} />
                                <span className="sidebar-nav-label">{item.label}</span>
                            </NavLink>
                        )
                    })}
                </nav>

                <div className="sidebar-footer">
                    <button className="sidebar-nav-item" onClick={handleLogout}>
                        <LogOut size={20} />
                        <span className="sidebar-nav-label">{t('auth.logout')}</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <div className="main-wrapper">
                {/* Header */}
                <header className="header">
                    <div className="header-left">
                        <button
                            className="header-menu-btn"
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                        >
                            <Menu size={20} />
                        </button>
                    </div>

                    <div className="header-right">
                        {/* Theme Toggle */}
                        <button className="header-btn" onClick={toggleTheme}>
                            {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
                        </button>

                        {/* Language Selector */}
                        <div className="header-dropdown">
                            <button
                                className="header-btn"
                                onClick={() => setLangMenuOpen(!langMenuOpen)}
                            >
                                <Globe size={18} />
                                <span>{i18n.language.toUpperCase()}</span>
                                <ChevronDown size={14} />
                            </button>
                            {langMenuOpen && (
                                <>
                                    <div className="dropdown-overlay" onClick={() => setLangMenuOpen(false)} />
                                    <div className="dropdown-menu">
                                        <button
                                            className={`dropdown-item ${i18n.language === 'fr' ? 'active' : ''}`}
                                            onClick={() => changeLanguage('fr')}
                                        >
                                            🇫🇷 Français
                                        </button>
                                        <button
                                            className={`dropdown-item ${i18n.language === 'en' ? 'active' : ''}`}
                                            onClick={() => changeLanguage('en')}
                                        >
                                            🇬🇧 English
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* User Menu */}
                        <div className="header-dropdown">
                            <button
                                className="header-user-btn"
                                onClick={() => setUserMenuOpen(!userMenuOpen)}
                            >
                                <div className="header-avatar">
                                    {user?.prenom?.[0]}{user?.nom?.[0]}
                                </div>
                                <div className="header-user-info">
                                    <span className="header-user-name">{user?.prenom} {user?.nom}</span>
                                    <span className="header-user-role">{user?.profil?.nom_profil}</span>
                                </div>
                                <ChevronDown size={14} />
                            </button>
                            {userMenuOpen && (
                                <>
                                    <div className="dropdown-overlay" onClick={() => setUserMenuOpen(false)} />
                                    <div className="dropdown-menu">
                                        <div className="dropdown-header">
                                            <strong>{user?.prenom} {user?.nom}</strong>
                                            <small>{user?.email}</small>
                                        </div>
                                        <div className="dropdown-divider" />
                                        <button className="dropdown-item danger" onClick={handleLogout}>
                                            <LogOut size={16} />
                                            {t('auth.logout')}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </header>

                {/* Main Content */}
                <main className="main-content">
                    <Outlet />
                </main>
            </div>
        </div>
    )
}
